import React from 'react';
import { Box, Paper, Typography, Chip, Alert, Divider, Stack, IconButton, Tooltip } from '@mui/material';
import {
  Event as DateIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

// El desglose "desde la última entrega", compartido por el panel del técnico y el del admin.
// Es un componente y no dos bloques parecidos porque su razón de ser es que ambos miren
// EXACTAMENTE la misma pantalla cuando no coinciden las cuentas.
//
// `enPrimeraPersona` solo cambia el trato: el técnico lee "tienes", el admin lee "tiene".

function fmt(n: number) {
  return (n ?? 0).toLocaleString('es-DO', { minimumFractionDigits: 0 });
}

// Con hora: cuando se discute "eso lo instalé después de entregar", el día solo no basta.
function fechaHora(d: string) {
  return new Date(d).toLocaleString('es-DO', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

interface Props {
  periodo: any;
  enPrimeraPersona?: boolean;
  // Solo el admin puede corregir un cobro, así que el botón aparece únicamente cuando la
  // pantalla que monta el componente pasa esta función. El técnico nunca la pasa.
  onCorregir?: (instalacion: any) => void;
}

// Una línea de la cuenta. `signo` va delante del monto para que se lea como una suma.
const Linea: React.FC<{ texto: React.ReactNode; monto: number; signo?: '+' | '−' | '='; color?: string; fuerte?: boolean; grande?: boolean }> =
  ({ texto, monto, signo, color, fuerte, grande }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}>
      <Typography variant={grande ? 'h6' : 'body2'} fontWeight={fuerte ? 700 : 400} color={fuerte ? 'text.primary' : 'text.secondary'}>
        {texto}
      </Typography>
      <Typography variant={grande ? 'h6' : 'body2'} fontWeight={fuerte ? 700 : 600} color={color} sx={{ whiteSpace: 'nowrap' }}>
        {signo && signo !== '=' ? `${signo} ` : ''}RD${fmt(monto)}
      </Typography>
    </Box>
  );

const PeriodoDesdeEntrega: React.FC<Props> = ({ periodo, enPrimeraPersona = false, onCorregir }) => {
  if (!periodo) return null;

  const yo = enPrimeraPersona;
  const t = periodo.totales;
  const otros: any[] = periodo.otrosMovimientos || [];
  const efectivo = periodo.balance.cashOwedToCompany || 0;
  const comisiones = periodo.balance.commissionsOwedByCompany || 0;
  const neto = efectivo - comisiones;

  return (
    <Paper sx={{ mb: 3, p: { xs: 1.5, sm: 2 } }}>
      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DateIcon color="primary" />
        {yo ? 'Desde tu última entrega' : `Desde la última entrega de ${periodo.tecnico?.nombre || 'el técnico'}`}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {periodo.desde
          ? `${yo ? 'Quedaste' : 'Quedó'} en cero el ${fechaHora(periodo.desde)}. Esto es todo lo que ha pasado después.`
          : `Todavía no ${yo ? 'has entregado' : 'ha entregado'} efectivo. Esto es todo lo que ${yo ? 'llevas' : 'lleva'}.`}
      </Typography>

      {periodo.entregaPendiente && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Hay una entrega de <strong>RD${fmt(periodo.entregaPendiente.amount)}</strong> a{' '}
          <strong>{periodo.entregaPendiente.deliveredToName}</strong> del{' '}
          {fechaHora(periodo.entregaPendiente.transactionDate)} esperando aprobación.
          {!yo && ' Hasta que se apruebe, el efectivo sigue contando como suyo.'}
        </Alert>
      )}

      {periodo.instalaciones.length === 0 && otros.length === 0 ? (
        <Alert severity="success" icon={<CheckIcon />}>
          No {yo ? 'has instalado' : 'ha instalado'} nada desde entonces.
        </Alert>
      ) : (
        <Stack spacing={1}>
          {periodo.instalaciones.map((i: any) => (
            <Box
              key={i.installationId}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider',
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{i.cliente}</Typography>
                <Typography variant="caption" color="text.secondary">{fechaHora(i.fecha)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography variant="body2" fontWeight={700}>RD${fmt(i.monto)}</Typography>
                {/* En naranja lo que quedó en su mano; en gris lo que fue directo a Driports. */}
                <Chip
                  size="small"
                  label={i.metodoTexto}
                  color={i.loTieneElTecnico ? 'warning' : 'default'}
                  sx={{ height: 18, fontSize: '0.68rem' }}
                />
              </Box>
              {onCorregir && (
                <Tooltip title="Corregir el cobro (monto o quién recibió el dinero)">
                  <IconButton size="small" onClick={() => onCorregir(i)} sx={{ flexShrink: 0 }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {/* Cobros que no son de instalaciones: renovaciones pagadas al técnico, registros a mano. */}
      {otros.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {otros.map((m: any, idx: number) => (
            <Box
              key={idx}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25,
                borderRadius: 2, border: '1px dashed', borderColor: 'warning.light',
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>{m.descripcion}</Typography>
                <Typography variant="caption" color="text.secondary">{fechaHora(m.fecha)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography variant="body2" fontWeight={700} color={m.monto < 0 ? 'error.main' : undefined}>
                  {m.monto < 0 ? '−' : ''}RD${fmt(Math.abs(m.monto))}
                </Typography>
                <Chip
                  size="small"
                  label={m.tipo === 'renovacion' ? 'Renovación' : m.tipo === 'ajuste' ? 'Ajuste' : 'Registrado a mano'}
                  color="warning"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.68rem' }}
                />
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      {periodo.entregas?.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {periodo.entregas.map((e: any, idx: number) => (
            <Box
              key={idx}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25,
                borderRadius: 2, border: '1px solid', borderColor: 'success.light',
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {yo ? 'Entregaste' : 'Entregó'}{' '}
                  {e.metodo === 'bank_transfer' ? 'por transferencia' : 'en efectivo'}
                  {e.recibidoPor ? ` a ${e.recibidoPor}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">{fechaHora(e.fecha)}</Typography>
              </Box>
              <Typography variant="body2" fontWeight={700} color="success.main" sx={{ flexShrink: 0 }}>
                −RD${fmt(e.monto)}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />

      {/* La cuenta en orden, de arriba abajo, y lo último es lo que se entrega. */}
      <Stack spacing={0.75}>
        <Linea texto={`${yo ? 'Cobraste' : 'Cobró'} en instalaciones (efectivo + transferencias a ${yo ? 'tu' : 'su'} cuenta)`} monto={t.cobradoEnEfectivo} />
        {otros.length > 0 && (
          <Linea texto={`Otros cobros (${otros.length})`} monto={t.otros} signo="+" />
        )}
        {t.gastos > 0 && (
          <Linea texto={`${t.gastosCount} ${t.gastosCount === 1 ? 'gasto' : 'gastos'}`} monto={t.gastos} signo="−" color="error.main" />
        )}
        {t.entregado > 0 && (
          <Linea texto={`Lo que ya ${yo ? 'entregaste' : 'entregó'}`} monto={t.entregado} signo="−" color="success.main" />
        )}
        <Divider />
        <Linea texto={`Efectivo de Driports en ${yo ? 'tu' : 'su'} mano`} monto={efectivo} signo="=" fuerte />
        {comisiones > 0 && (
          <Linea texto={`${yo ? 'Tu comisión' : 'Su comisión'}, que ${yo ? 'te cobras' : 'se cobra'} de ese dinero`} monto={comisiones} signo="−" color="info.main" />
        )}
        <Divider sx={{ borderBottomWidth: 2 }} />
        {neto >= 0 ? (
          <Linea texto={yo ? 'Tienes que entregar' : 'Tiene que entregar'} monto={neto} signo="=" color="warning.main" fuerte grande />
        ) : (
          <Linea texto={yo ? 'Driports te debe' : 'Driports le debe'} monto={-neto} signo="=" color="info.main" fuerte grande />
        )}
      </Stack>

      {!periodo.cuadra && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Esta cuenta da RD${fmt(t.deberiaTener)} pero el saldo dice RD$
          {fmt(periodo.balance.cashOwedToCompany)}.{' '}
          {yo
            ? 'Enséñale esta pantalla al admin para que lo revisen juntos.'
            : 'Hay movimientos registrados por fuera del flujo normal; revísalo con el técnico sobre esta misma pantalla.'}
        </Alert>
      )}
    </Paper>
  );
};

export default PeriodoDesdeEntrega;
